import { lowerCamelCase, objectKeyCount, pluralize, upperCamelCase } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import AddIcon from '@mui/icons-material/Add';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { formatDate } from '@kaiizen/kai-presenters';
import { ApiScanTemplate } from '@kaiizen/kai-pro-api';
import { useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import {
  createTemplateGroup,
  getScanTemplateLastUpdatedAt,
  ScanTemplateGroup,
  ScanTemplateRoot,
  ScanTemplateTask,
  ScanTemplateValidationDetails,
  ScanTemplateValidationError,
  STANDARD_ROOM_GROUP_VALUES,
  validateStandardTemplate,
} from '@kaiizen/kai-scan-template-system';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import { logError } from '../../Lib/logger';
import { ScanTemplateDataMutators, ScanTemplateMutators } from '../../State';
import { LAYOUT2, TYPOGRAPHY2 } from '../../Styles';
import { LabeledCard, LabeledInput, ScanEditorPageTitleBar } from '../../Widgets';
import { MAX_PART_DESCRIPTION_LENGTH, MAX_TASK_NAME_LENGTH } from './scanTemplateFieldLimits';
import { ScanTemplateFilterFunctions } from './ScanTemplateFilterCache';
import { ScanTemplateGroupMeasurementsEditor } from './ScanTemplateGroupMeasurementsEditor';

const TEMPLATE_OVERVIEW_STYLE = css`
  label: TEMPLATE_OVERVIEW_STYLE;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  background-color: var(--background2);
`;

const CONTENT_STYLE = css`
  label: CONTENT_STYLE;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: ${LAYOUT2.spacing2}px;
  gap: ${LAYOUT2.spacing2}px;
  overflow-y: auto;
  align-items: center;

  &::after {
    content: '';
    display: block;
    height: ${LAYOUT2.spacing4}px;
    flex: 0 0 ${LAYOUT2.spacing4}px;
    width: 100%;
    pointer-events: none;
  }
`;

const TWO_COLUMN_LAYOUT_STYLE = css`
  label: TWO_COLUMN_LAYOUT_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacing2}px;
  width: 100%;
  max-width: 1000px;
`;

const LEFT_COLUMN_STYLE = css`
  label: LEFT_COLUMN_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing2}px;
  flex: 1;
  min-height: 0;
`;

const RIGHT_COLUMN_STYLE = css`
  label: RIGHT_COLUMN_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing2}px;
  flex: 1;
  min-height: 0;
`;

const STATS_CARD_STYLE = css`
  label: STATS_CARD_STYLE;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${LAYOUT2.spacing2}px;
`;

const STAT_ITEM_STYLE = css`
  label: STAT_ITEM_STYLE;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const STAT_VALUE_STYLE = css`
  label: STAT_VALUE_STYLE;
  ${TYPOGRAPHY2.large}
  font-weight: 600;
  color: var(--text1);
`;

const STAT_LABEL_STYLE = css`
  label: STAT_LABEL_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
  text-transform: uppercase;
`;

const EDIT_CARD_STYLE = css`
  label: EDIT_CARD_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing2}px;
`;

const GROUPS_SECTION_STYLE = css`
  label: GROUPS_SECTION_STYLE;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing2}px;
`;

const CATEGORY_LABEL_STYLE = css`
  label: CATEGORY_LABEL_STYLE;
  ${TYPOGRAPHY2.mediumBold}
  color: var(--text2);
  margin-top: ${LAYOUT2.spacing2}px;
  margin-bottom: ${LAYOUT2.spacing1}px;
  text-transform: uppercase;
`;

const GROUPS_GRID_STYLE = css`
  label: GROUPS_GRID_STYLE;
  display: flex;
  flex-wrap: wrap;
  gap: ${LAYOUT2.spacing2}px;
`;

const GROUP_CARD_STYLE = css`
  label: GROUP_CARD_STYLE;
  width: ${LAYOUT2.spacing * 26}px;
  height: ${LAYOUT2.spacing * 12}px;
  background: var(--altBackgroundForDarkMode1);
  display: flex;
  flex-direction: column;
  padding: ${LAYOUT2.spacing2}px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: var(--altBackgroundForDarkMode3);
  }
`;

const GROUP_CARD_TITLE_STYLE = css`
  label: GROUP_CARD_TITLE_STYLE;
  ${TYPOGRAPHY2.mediumBold}
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: ${LAYOUT2.spacing1}px;
`;

const GROUP_CARD_DETAIL_STYLE = css`
  label: GROUP_CARD_DETAIL_STYLE;
  ${TYPOGRAPHY2.small}
  color: var(--text2);
`;

const ADD_GROUP_BUTTON_STYLE = css`
  label: ADD_GROUP_BUTTON_STYLE;
  width: ${LAYOUT2.spacing * 26}px;
  height: ${LAYOUT2.spacing * 12}px;
  background: var(--background2);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  gap: ${LAYOUT2.spacing1}px;
  transition: background-color 0.3s ease;
  border: 2px dashed var(--border2);

  &:hover {
    background: var(--background3);
    border-color: var(--border1);
  }
`;

const ADD_GROUP_BUTTON_TEXT_STYLE = css`
  label: ADD_GROUP_BUTTON_TEXT_STYLE;
  ${TYPOGRAPHY2.medium}
  color: var(--text2);
`;

const WARNINGS_LIST_STYLE = css`
  label: WARNINGS_LIST_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
`;

const WARNING_ITEM_STYLE = css`
  label: WARNING_ITEM_STYLE;
  background: var(--backgroundYellow2);
  border: 1px solid var(--border2);
  color: var(--text1);
  padding: ${LAYOUT2.spacing1}px;
  gap: ${LAYOUT2.spacing1}px;
  display: flex;
  align-items: flex-end;
  position: relative;
`;

const WARNING_TEXT_STYLE = css`
  label: WARNING_TEXT_STYLE;
  ${TYPOGRAPHY2.small}
  color: var(--text1);
`;

const WARNING_ACTIONS_STYLE = css`
  label: WARNING_ACTIONS_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacing1}px;
`;

const WARNING_FIX_BUTTON_STYLE = css`
  label: WARNING_FIX_BUTTON_STYLE;
  ${TYPOGRAPHY2.smallBold}
  color: var(--text1);
  background: var(--backgroundYellow1);
  border: 1px solid var(--border2);
  padding: 4px 8px;
  cursor: pointer;
  display: inline-block;
  font-size: ${pxToRem(12)};
  line-height: 1.2;
`;

const DEFAULT_CATEGORY_NAME = 'other';

const groupByCategory = (groups: ScanTemplateGroup[]): Map<string | null, ScanTemplateGroup[]> => {
  const categoryMap = new Map<string | null, ScanTemplateGroup[]>();
  categoryMap.set('interior', []);
  categoryMap.set('exterior', []);
  categoryMap.set(DEFAULT_CATEGORY_NAME, []);

  for (const group of groups) {
    const category = lowerCamelCase(group.category || DEFAULT_CATEGORY_NAME);
    const categoryGroups = categoryMap.get(category) || [];
    categoryGroups.push(group);
    categoryMap.set(category, categoryGroups);
  }

  // Sort groups within each category by name
  for (const [, categoryGroups] of categoryMap) {
    categoryGroups.sort((a, b) => a.name.localeCompare(b.name));
  }

  return categoryMap;
};

const CategorySection = ({
  category,
  groups,
  templateRoot,
  filteredOptionCountsByTaskId,
  filteredLineItemCountsByTaskId,
  onGroupClick,
  onAddGroup,
  readOnly,
}: {
  category: string | null;
  groups: ScanTemplateGroup[];
  templateRoot: ScanTemplateRoot;
  filteredOptionCountsByTaskId: Record<string, number>;
  filteredLineItemCountsByTaskId: Record<string, number>;
  onGroupClick: (groupId: string) => void;
  onAddGroup: (category: string | null) => void;
  readOnly?: boolean;
}) => {
  const renderGroupCardStats = (count: number, filteredCount: number, label: string) => (
    <div css={GROUP_CARD_DETAIL_STYLE}>
      {filteredCount < count ? `${filteredCount} / ${count} ${pluralize(label)}` : pluralize(count, label)}
    </div>
  );

  const categoryLabel = category || DEFAULT_CATEGORY_NAME;
  const addButtonLabel = `Add ${upperCamelCase(categoryLabel, ' ')} Group`;

  return (
    <div>
      <div css={CATEGORY_LABEL_STYLE}>
        {categoryLabel} Groups
      </div>
      <div css={GROUPS_GRID_STYLE}>
        {groups.map(group => {
          const { taskCount, optionCount, lineItemCount, filteredTaskCount, filteredOptionCount, filteredLineItemCount } = getFilteredGroupTaskCounts(
            templateRoot,
            group,
            filteredOptionCountsByTaskId,
            filteredLineItemCountsByTaskId,
          );
          return (
            <div
              key={group.id}
              css={GROUP_CARD_STYLE}
              onClick={() => onGroupClick(group.id)}
            >
              <div css={GROUP_CARD_TITLE_STYLE}>{group.name}</div>
              {renderGroupCardStats(taskCount, filteredTaskCount, 'task')}
              {renderGroupCardStats(optionCount, filteredOptionCount, 'option')}
              {renderGroupCardStats(lineItemCount, filteredLineItemCount, 'line item')}
            </div>
          );
        })}
        {!readOnly && (
          <div css={ADD_GROUP_BUTTON_STYLE} onClick={() => onAddGroup(category)}>
            <AddIcon sx={{ color: 'var(--text2)', fontSize: pxToRem(32) }} />
            <div css={ADD_GROUP_BUTTON_TEXT_STYLE}>{addButtonLabel}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const getFilteredGroupTaskCounts = (
  scanTemplateRoot: ScanTemplateRoot,
  group: ScanTemplateGroup,
  filteredOptionCountsByTaskId: Record<string, number>,
  filteredLineItemCountsByTaskId: Record<string, number>,
) => {
  let taskCount = 0, filteredTaskCount = 0;
  let optionCount = 0, filteredOptionCount = 0;
  let lineItemCount = 0, filteredLineItemCount = 0;
  for (const taskId of group.taskTemplateIds || []) {
    const task = scanTemplateRoot.taskTemplatesById?.[taskId];
    if (!task) continue;
    taskCount += 1;
    filteredTaskCount += filteredOptionCountsByTaskId[taskId] || 0;
    optionCount += task.optionTemplates?.length || 0;
    filteredOptionCount += filteredOptionCountsByTaskId[taskId] || 0;
    for (const option of task.optionTemplates || []) {
      lineItemCount += option.lineItemTemplates?.length || 0;
      filteredLineItemCount += filteredLineItemCountsByTaskId[taskId] || 0;
    }
  }

  return { taskCount, filteredTaskCount, optionCount, filteredOptionCount, lineItemCount, filteredLineItemCount };
};

export const ScanTemplateOverview = ({
  filteredGroups,
  filteredTasksById,
  scanTemplateFilterFunctions,
  templateRoot,
  scanTemplate,
  scanTemplateDataMutators,
  scanTemplateMutators,
  readOnly,
}: {
  scanTemplateFilterFunctions: ScanTemplateFilterFunctions;
  templateRoot: ScanTemplateRoot;
  scanTemplate: ApiScanTemplate;
  scanTemplateDataMutators: ScanTemplateDataMutators;
  scanTemplateMutators: ScanTemplateMutators;
  filteredGroups: ScanTemplateGroup[];
  filteredTasksById: Record<string, ScanTemplateTask>;
  readOnly?: boolean;
}) => {
  const navigate = useNavigate();
  const { featureFlags } = useFeatureFlags();
  const groupCount = templateRoot.groupTemplates?.length || 0;
  const filteredGroupCount = filteredGroups.length;
  const taskCount = templateRoot.taskTemplatesById ? Object.keys(templateRoot.taskTemplatesById).length : 0;
  const filteredTaskCount = objectKeyCount(filteredTasksById);
  const [optionCount, lineItemCount] = useMemo(() => {
    let optionCount = 0;
    let lineItemCount = 0;
    for (const taskId in templateRoot.taskTemplatesById) {
      const task = templateRoot.taskTemplatesById[taskId];
      optionCount += task.optionTemplates?.length || 0;
      for (const option of task.optionTemplates || []) {
        lineItemCount += option.lineItemTemplates?.length || 0;
      }
    }
    return [optionCount, lineItemCount];
  }, [templateRoot.taskTemplatesById]);
  const lastUpdatedAt = useMemo(() => getScanTemplateLastUpdatedAt(templateRoot), [templateRoot]);

  const [filteredOptionCount, filteredLineItemCount, filteredOptionCountsByTaskId, filteredLineItemCountsByTaskId] = useMemo(() => {
    let filteredOptionCount = 0;
    let filteredLineItemCount = 0;
    const filteredOptionCountsByTaskId: Record<string, number> = {};
    const filteredLineItemCountsByTaskId: Record<string, number> = {};
    for (const taskId in filteredTasksById) {
      const task = filteredTasksById[taskId];
      for (const option of task.optionTemplates || []) {
        if (scanTemplateFilterFunctions.filterScanTemplateOption(option)) {
          filteredOptionCount += 1;
          filteredOptionCountsByTaskId[taskId] = (filteredOptionCountsByTaskId[taskId] || 0) + 1;
        }
        for (const lineItem of option.lineItemTemplates || []) {
          if (scanTemplateFilterFunctions.filterScanTemplateLineItem(lineItem)) {
            filteredLineItemCount += 1;
            filteredLineItemCountsByTaskId[taskId] = (filteredLineItemCountsByTaskId[taskId] || 0) + 1;
          }
        }
      }
    }
    return [filteredOptionCount, filteredLineItemCount, filteredOptionCountsByTaskId, filteredLineItemCountsByTaskId];
  }, [filteredTasksById, scanTemplateFilterFunctions]);

  const scanTemplateId = scanTemplate?.id;
  const validationErrors: ScanTemplateValidationDetails[] = useMemo(() => {
    try {
      validateStandardTemplate(templateRoot, { strict: true });
      return [];
    } catch (error) {
      if (error instanceof ScanTemplateValidationError) {
        return error.details.map(({ message, ...rest }) => ({
          message: message.replace(/\s*\(?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\)?\s*/gi, ''),
          ...rest,
        }));
      } else {
        logError('Template validation error', error, { templateId: scanTemplateId });
        const message = (error instanceof Error ? error.message : String(error)) || '';
        const lines = message.split('\n');
        const detailLines = lines.slice(1).filter(Boolean);
        return detailLines.map(message => ({ message }));
      }
    }
  }, [templateRoot, scanTemplateId]);

  const renderStat = (count: number, filteredCount: number, label: string) => {
    return (
      <div css={STAT_ITEM_STYLE}>
        <div css={STAT_VALUE_STYLE}>{filteredCount < count ? `${filteredCount} / ${count}` : count}</div>
        <div css={STAT_LABEL_STYLE}>{filteredCount < count ? 'Filtered' : ''} {label}</div>
      </div>
    );
  };

  const handleAddGroup = (category: string | null) => {
    const newGroup = createTemplateGroup({
      name: category ? `New ${upperCamelCase(category, ' ')} Group` : 'New Group',
      category: category || undefined,
      values: category === 'interior' ? STANDARD_ROOM_GROUP_VALUES : undefined,
    });
    scanTemplateDataMutators.addGroup(newGroup);
    setTimeout(() => navigate(`/template/${scanTemplate.id}?groupId=${newGroup.id}`), 100);
  };

  const groupedByCategory = useMemo(() => groupByCategory(filteredGroups), [filteredGroups]);

  const sortedCategories = useMemo(() => {
    const categories = Array.from(groupedByCategory.keys());
    // Sort categories alphabetically, with null (uncategorized) at the end
    return categories.sort((a, b) => {
      if (a === null) return 1;
      if (b === null) return -1;
      return a.localeCompare(b);
    });
  }, [groupedByCategory]);

  return (
    <div css={TEMPLATE_OVERVIEW_STYLE}>
      <ScanEditorPageTitleBar
        title='Template Overview'
        buttons={[]}
      />

      <div css={CONTENT_STYLE}>
        <div css={TWO_COLUMN_LAYOUT_STYLE}>
          <div css={LEFT_COLUMN_STYLE}>
            <LabeledCard title='Template Information'>
              <div css={STATS_CARD_STYLE}>
                {renderStat(groupCount, filteredGroupCount, 'Groups')}
                {renderStat(taskCount, filteredTaskCount, 'Tasks')}
                {renderStat(optionCount, filteredOptionCount, 'Options')}
                {renderStat(lineItemCount, filteredLineItemCount, 'Line Items')}
                <div css={STAT_ITEM_STYLE}>
                  <div css={STAT_VALUE_STYLE}>{formatDate(scanTemplate.createdAt)}</div>
                  <div css={STAT_LABEL_STYLE}>Created</div>
                </div>
                <div css={STAT_ITEM_STYLE}>
                  <div css={STAT_VALUE_STYLE}>{formatDate(lastUpdatedAt)}</div>
                  <div css={STAT_LABEL_STYLE}>Updated</div>
                </div>
              </div>
            </LabeledCard>

            {validationErrors.length > 0 && (
              <LabeledCard title='Template Validation Errors'>
                <div css={WARNINGS_LIST_STYLE}>
                  {validationErrors.map((error, idx) => {
                    return (
                      <div key={idx} css={WARNING_ITEM_STYLE}>
                        <div css={WARNING_TEXT_STYLE}>{error.message}</div>
                        <div css={WARNING_ACTIONS_STYLE}>
                          {(error.groupId || error.taskId || error.optionId || error.lineItemId) && (
                            <button
                              css={WARNING_FIX_BUTTON_STYLE}
                              onClick={() =>
                                navigate(`/template/${scanTemplate.id}${
                                  (() => {
                                    const q: string[] = [];
                                    error.groupId && q.push(`groupId=${error.groupId}`);
                                    error.taskId && q.push(`taskId=${error.taskId}`);
                                    error.optionId && q.push(`optionId=${error.optionId}`);
                                    error.lineItemId && q.push(`lineItemId=${error.lineItemId}`);
                                    return q.length ? `?${q.join('&')}` : '';
                                  })()
                                }`)}
                            >
                              Fix
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </LabeledCard>
            )}
          </div>

          <div css={RIGHT_COLUMN_STYLE}>
            <LabeledCard title='Template Details'>
              <div css={EDIT_CARD_STYLE}>
                <LabeledInput
                  label='Name'
                  value={templateRoot.name}
                  placeholder='Template name'
                  maxLength={MAX_TASK_NAME_LENGTH}
                  showCharacterCount={true}
                  disabled={readOnly}
                  onUpdate={async name => {
                    await scanTemplateDataMutators.updateTemplate({ name });
                    await scanTemplateMutators.update(scanTemplate.id, { name });
                    trackEvent(ANALYTICS_EVENTS.scanTemplateUpdated, {
                      templateId: scanTemplate.id,
                      name,
                    });
                  }}
                />
                <LabeledInput
                  label='Description'
                  value={templateRoot.description || ''}
                  placeholder='Template description'
                  multiline
                  rows={3}
                  maxLength={MAX_PART_DESCRIPTION_LENGTH}
                  showCharacterCount={true}
                  disabled={readOnly}
                  onUpdate={async description => {
                    await scanTemplateDataMutators.updateTemplate({ description });
                    await scanTemplateMutators.update(scanTemplate.id, { description });
                    trackEvent(ANALYTICS_EVENTS.scanTemplateUpdated, {
                      templateId: scanTemplate.id,
                      description,
                    });
                  }}
                />
              </div>
            </LabeledCard>
          </div>
        </div>

        <div css={GROUPS_SECTION_STYLE}>
          {sortedCategories.map(category => (
            <CategorySection
              key={category || 'uncategorized'}
              category={category}
              groups={groupedByCategory.get(category)!}
              templateRoot={templateRoot}
              filteredOptionCountsByTaskId={filteredOptionCountsByTaskId}
              filteredLineItemCountsByTaskId={filteredLineItemCountsByTaskId}
              onGroupClick={groupId => navigate(`/template/${scanTemplate.id}?groupId=${groupId}`)}
              onAddGroup={handleAddGroup}
              readOnly={readOnly}
            />
          ))}
        </div>

        {!!featureFlags.useRoomMeasurements && (
          <div css={GROUPS_SECTION_STYLE}>
            <LabeledCard title='Room Measurements'>
              <ScanTemplateGroupMeasurementsEditor
                templateRoot={templateRoot}
                scanTemplateDataMutators={scanTemplateDataMutators}
                onGroupClick={groupId => navigate(`/template/${scanTemplate.id}?groupId=${groupId}`)}
                readOnly={readOnly}
              />
            </LabeledCard>
          </div>
        )}
      </div>
    </div>
  );
};
